/**
 * Set Password Form Module
 * Sets a new password from an invite/forgot token, then signs the user in.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSetPassword } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { Spinner } from '@/components/ui/spinner';
import { useNavigate } from 'react-router';

// Mirrors the backend validatePassword rule: >=8 chars, upper, number, special.
const setPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(128, 'Password is too long')
      .regex(/[A-Z]/, 'Password must include an uppercase letter')
      .regex(/\d/, 'Password must include a number')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must include a special character'),
    confirmPassword: z.string().min(1, 'Please confirm your password')
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword']
  });

type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

interface SetPasswordFormProps {
  token: string;
}

export function SetPasswordForm({ token }: SetPasswordFormProps) {
  const navigate = useNavigate();
  const setPasswordMutation = useSetPassword();

  const form = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });

  const onSubmit = async (data: SetPasswordFormData) => {
    try {
      await setPasswordMutation.mutateAsync({ token, password: data.password });
      toast.success('Password set successfully!');
      // Wait a tick so auth state propagates before navigating.
      await new Promise((resolve) => setTimeout(resolve, 0));
      navigate('/', { replace: true });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to set password. The link may have expired.';
      toast.error(errorMessage);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel>New password</FormLabel>
              <FormControl>
                <Input type='password' placeholder='Enter a new password' autoComplete='new-password' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='confirmPassword'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm password</FormLabel>
              <FormControl>
                <Input type='password' placeholder='Re-enter your password' autoComplete='new-password' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type='submit' className='w-full' variant={'outline'} disabled={setPasswordMutation.isPending}>
          {setPasswordMutation.isPending ? (
            <>
              <Spinner className='mr-2' />
              Setting password...
            </>
          ) : (
            'Set password'
          )}
        </Button>
      </form>
    </Form>
  );
}
